import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeywordTagInput } from "@/components/admin/KeywordTagInput";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit, Save, X, RefreshCw, FileText, Target, Check, AlertTriangle, Search, Bold, Italic, Type, Eye, EyeOff, Link2 } from "lucide-react";
import axios from "axios";
import PostContent from "@/components/PostContent";
import { API } from "@/lib/api";

const emptyForm = {
  title: "",
  content: "",
  excerpt: "",
  meta_description: "",
  focus_keyword: "",
  secondary_keywords: [],
  image_url: "",
  image_alt: "",
  content_font: "",
  author: "Laura A. Jeanis, LCSW, LICSW, CCTP, CEO",
};

// === SEO analysis helpers ===
const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
const normalizeForMatch = (s) => stripHtml(s).toLowerCase().replace(/[-_/]+/g, " ").replace(/\s+/g, " ");
const countWords = (s) => {
  const t = stripHtml(s);
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
};
const containsKeyword = (text, kw) => {
  if (!kw) return false;
  const haystack = normalizeForMatch(text);
  const needle = normalizeForMatch(kw);
  return needle.length > 0 && haystack.includes(needle);
};
const keywordOccurrences = (text, kw) => {
  if (!kw) return 0;
  const haystack = normalizeForMatch(text);
  const needle = normalizeForMatch(kw);
  if (!needle) return 0;
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matches = haystack.match(new RegExp(escaped, "g"));
  return matches ? matches.length : 0;
};
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
const firstParagraph = (content) => {
  const plain = stripHtml(content);
  return plain.split(/\n\n|\.\s/)[0] || "";
};

// === SEO Analysis Panel (live checklist + Google preview) ===
const CheckRow = ({ ok, label, hint }) => (
  <div className="flex items-start gap-2 py-1.5" data-testid={`seo-check-${ok ? "pass" : "fail"}`}>
    {ok ? (
      <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
    ) : (
      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
    )}
    <div className="flex-1 min-w-0">
      <p className={`text-sm ${ok ? "text-foreground" : "text-foreground/80"}`}>{label}</p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  </div>
);

const SeoAnalysisPanel = ({ form }) => {
  const kw = (form.focus_keyword || "").trim();
  const hasKw = kw.length > 0;
  const slugGuess = slugify(form.title);
  const contentWords = countWords(form.content);
  const kwInTitle = containsKeyword(form.title, kw);
  const kwInMeta = containsKeyword(form.meta_description, kw);
  const kwInSlug = hasKw ? slugGuess.includes(slugify(kw)) : false;
  const kwInFirstParagraph = containsKeyword(firstParagraph(form.content), kw);
  const kwInAlt = containsKeyword(form.image_alt, kw);
  const kwCount = keywordOccurrences(form.content, kw);
  const density = contentWords > 0 ? ((kwCount / contentWords) * 100).toFixed(2) : "0.00";
  const densityNum = parseFloat(density);
  const densityOk = hasKw && densityNum >= 0.5 && densityNum <= 2.5;
  const metaLen = (form.meta_description || "").length;
  const metaLenOk = metaLen >= 120 && metaLen <= 160;
  const titleLen = (form.title || "").length;
  const titleLenOk = titleLen >= 30 && titleLen <= 60;
  const wordsOk = contentWords >= 600;
  const altPresent = (form.image_alt || "").trim().length > 0;

  // Score
  const checks = [
    hasKw,
    kwInTitle,
    kwInMeta,
    kwInSlug,
    kwInFirstParagraph,
    densityOk,
    metaLenOk,
    titleLenOk,
    wordsOk,
    !form.image_url || altPresent,
  ];
  const passed = checks.filter(Boolean).length;
  const total = checks.length;
  const scorePct = Math.round((passed / total) * 100);
  let scoreColor = "bg-amber-100 text-amber-800";
  if (scorePct >= 80) scoreColor = "bg-green-100 text-green-800";
  else if (scorePct < 50) scoreColor = "bg-red-100 text-red-800";

  // Google preview values
  const previewTitle = (form.title || "Your post title").slice(0, 60);
  const previewSlug = slugGuess || "your-post-slug";
  const previewDesc =
    (form.meta_description && form.meta_description.trim()) ||
    (form.excerpt && form.excerpt.trim()) ||
    "Your meta description will appear here. Write one for best results.";
  const previewDescTrimmed = previewDesc.length > 160 ? previewDesc.slice(0, 157) + "..." : previewDesc;

  return (
    <div
      className="rounded-2xl border border-border bg-secondary/30 p-5 space-y-5"
      data-testid="seo-analysis-panel"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          <h3 className="font-serif text-lg font-semibold text-foreground">SEO Analysis</h3>
        </div>
        <Badge className={`${scoreColor} border-0`} data-testid="seo-score-badge">
          Score: {passed}/{total} · {scorePct}%
        </Badge>
      </div>

      {!hasKw && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          Add a <strong>Focus Keyword</strong> above to unlock the full SEO analysis.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        <CheckRow
          ok={hasKw}
          label="Focus keyword is set"
          hint={hasKw ? `"${kw}"` : "Set the main phrase you want this post to rank for."}
        />
        <CheckRow
          ok={kwInTitle}
          label="Keyword appears in the Title"
          hint={hasKw && !kwInTitle ? "Try to include the focus keyword in the title (ideally near the start)." : null}
        />
        <CheckRow
          ok={kwInMeta}
          label="Keyword appears in the Meta Description"
          hint={hasKw && !kwInMeta ? "Add the keyword once, naturally, in your meta description." : null}
        />
        <CheckRow
          ok={kwInSlug}
          label="Keyword appears in the URL slug"
          hint={hasKw && !kwInSlug ? `Current slug: /${previewSlug}` : null}
        />
        <CheckRow
          ok={kwInFirstParagraph}
          label="Keyword appears in the first paragraph"
          hint={hasKw && !kwInFirstParagraph ? "Mention the keyword in the opening paragraph for stronger relevance." : null}
        />
        <CheckRow
          ok={densityOk}
          label={`Keyword density (${density}%, ${kwCount} mention${kwCount === 1 ? "" : "s"})`}
          hint={
            !hasKw
              ? null
              : densityNum < 0.5
              ? "Too low — aim for 0.5–2.5% (mention the keyword a few more times)."
              : densityNum > 2.5
              ? "Too high — Google may flag it as keyword stuffing."
              : "Great range (0.5%–2.5%)."
          }
        />
        <CheckRow
          ok={titleLenOk}
          label={`Title length (${titleLen} chars)`}
          hint={
            titleLen < 30
              ? "Too short — aim for 30–60 characters."
              : titleLen > 60
              ? "Too long — may get cut off in Google. Aim for 30–60."
              : "Perfect length for Google."
          }
        />
        <CheckRow
          ok={metaLenOk}
          label={`Meta description length (${metaLen} chars)`}
          hint={
            metaLen === 0
              ? "Add a meta description (120–160 characters is ideal)."
              : metaLen < 120
              ? "A bit short — aim for 120–160 characters."
              : metaLen > 160
              ? "A bit long — Google may truncate. Aim for 120–160."
              : "Perfect length for Google."
          }
        />
        <CheckRow
          ok={wordsOk}
          label={`Content length (${contentWords} words)`}
          hint={
            wordsOk
              ? "Long-form content gives you more ranking power."
              : "Aim for at least 600 words for stronger SEO."
          }
        />
        <CheckRow
          ok={!form.image_url || altPresent}
          label={form.image_url ? "Featured image has alt text" : "Featured image alt text (no image set)"}
          hint={
            form.image_url && !altPresent
              ? "Add alt text describing the image — helps Google Images and accessibility."
              : form.image_url && altPresent && hasKw && !kwInAlt
              ? "Consider including your focus keyword in the alt text if it fits naturally."
              : null
          }
        />
      </div>

      {/* Google search-result preview */}
      <div className="pt-2">
        <div className="flex items-center gap-2 mb-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Google Preview
          </p>
        </div>
        <div
          className="rounded-xl bg-white border border-border p-4"
          data-testid="seo-google-preview"
        >
          <p className="text-xs text-[#202124] truncate">
            divinelovewellness.net › blog › {previewSlug}
          </p>
          <p className="text-[#1a0dab] text-lg leading-snug mt-0.5 hover:underline cursor-default">
            {previewTitle} | Divine Love Wellness Enterprises
          </p>
          <p className="text-sm text-[#4d5156] mt-1 leading-snug">{previewDescTrimmed}</p>
        </div>
      </div>
    </div>
  );
};

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null); // null = list, "new" = create, "<slug>" = edit
  const [form, setForm] = useState(emptyForm);
  const [showPreview, setShowPreview] = useState(true);
  const contentRef = useRef(null);

  const wrapSelection = (before, after) => {
    const el = contentRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const current = form.content;
    const selected = current.slice(start, end) || "text";
    const next = current.slice(0, start) + before + selected + after + current.slice(end);
    setForm((prev) => ({ ...prev, content: next }));
    // restore cursor / selection over the inserted text
    requestAnimationFrame(() => {
      el.focus();
      const selStart = start + before.length;
      const selEnd = selStart + selected.length;
      el.setSelectionRange(selStart, selEnd);
    });
  };

  const setHeading = (level) => {
    const el = contentRef.current;
    if (!el) return;
    const current = form.content;
    const lineStart = current.lastIndexOf("\n", el.selectionStart - 1) + 1;
    const endIdx = current.indexOf("\n", Math.max(el.selectionEnd - (el.selectionEnd > el.selectionStart ? 1 : 0), el.selectionStart));
    const lineEnd = endIdx === -1 ? current.length : endIdx;
    const prefix = level ? `${"#".repeat(level)} ` : "";
    const block = current
      .slice(lineStart, lineEnd)
      .split("\n")
      .map((line) => {
        const plain = line.replace(/^#{2,3}\s+/, "").replace(/^<h([23])>([\s\S]*)<\/h\1>$/i, "$2");
        return plain.trim() ? prefix + plain : line;
      })
      .join("\n");
    setForm((prev) => ({ ...prev, content: current.slice(0, lineStart) + block + current.slice(lineEnd) }));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(lineStart, lineStart + block.length);
    });
  };

  const insertLink = () => {
    const el = contentRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const current = form.content;
    const selected = current.slice(start, end);
    const defaultLabel = selected || "link text";
    // eslint-disable-next-line no-alert
    const url = window.prompt(
      "Enter the URL. Use an internal path like /specialties/therapy-for-hyper-independence or a full URL like https://example.com",
      "/"
    );
    if (!url) return;
    let label = defaultLabel;
    if (!selected) {
      // eslint-disable-next-line no-alert
      const askedLabel = window.prompt("Link text to display:", "link text");
      if (!askedLabel) return;
      label = askedLabel;
    }
    const insertion = `[${label}](${url.trim()})`;
    const next = current.slice(0, start) + insertion + current.slice(end);
    setForm((prev) => ({ ...prev, content: next }));
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + insertion.length;
      el.setSelectionRange(caret, caret);
    });
  };
  const [saving, setSaving] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (e) {
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const startNew = () => {
    setForm(emptyForm);
    setEditingSlug("new");
  };

  const startEdit = (post) => {
    setForm({
      title: post.title || "",
      content: post.content || "",
      excerpt: post.excerpt || "",
      meta_description: post.meta_description || "",
      focus_keyword: post.focus_keyword || "",
      secondary_keywords: post.secondary_keywords || [],
      image_url: post.image_url || "",
      image_alt: post.image_alt || "",
      content_font: post.content_font || "",
      author: post.author || emptyForm.author,
    });
    setEditingSlug(post.slug);
  };

  const cancelEdit = () => {
    setEditingSlug(null);
    setForm(emptyForm);
  };

  const handleField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.title || form.title.length < 5) {
      toast.error("Title must be at least 5 characters");
      return;
    }
    if (!form.content || form.content.length < 50) {
      toast.error("Content must be at least 50 characters");
      return;
    }
    if (form.meta_description && form.meta_description.length > 170) {
      toast.error("SEO description must be 170 characters or less");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        content: form.content,
        excerpt: form.excerpt || undefined,
        meta_description: form.meta_description || undefined,
        focus_keyword: form.focus_keyword || undefined,
        secondary_keywords: form.secondary_keywords,
        image_url: form.image_url || undefined,
        image_alt: form.image_alt || undefined,
        content_font: form.content_font || undefined,
        author: form.author || undefined,
      };

      if (editingSlug === "new") {
        await axios.post(`${API}/blog`, payload);
        toast.success("Blog post created");
      } else {
        await axios.patch(`${API}/blog/${editingSlug}`, payload);
        toast.success("Blog post updated");
      }
      cancelEdit();
      fetchPosts();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save post";
      toast.error(typeof msg === "string" ? msg : "Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  const getSaveLabel = () => {
    if (saving) return "Saving...";
    if (editingSlug === "new") return "Create Post";
    return "Save Changes";
  };
  const charCount = (form.meta_description || "").length;
  const getCharCountColor = (count) => {
    if (count > 170) return "text-red-500";
    if (count > 160) return "text-amber-600";
    return "text-muted-foreground";
  };
  const charCountColor = getCharCountColor(charCount);

  // === Edit / Create Form ===
  if (editingSlug !== null) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        data-testid="blog-form"
      >
        <Card className="bg-white rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl font-semibold text-foreground">
              {editingSlug === "new" ? "Create New Blog Post" : "Edit Blog Post"}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelEdit}
              data-testid="cancel-edit-btn"
            >
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="post-title">Title *</Label>
              <Input
                id="post-title"
                value={form.title}
                onChange={handleField("title")}
                placeholder="Post title"
                className="rounded-xl"
                data-testid="post-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="post-focus-keyword" className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                Focus Keyword{" "}
                <span className="text-xs text-muted-foreground font-normal">
                  (the main phrase you want this post to rank for on Google)
                </span>
              </Label>
              <Input
                id="post-focus-keyword"
                value={form.focus_keyword}
                onChange={handleField("focus_keyword")}
                placeholder="e.g. ketamine assisted psychotherapy Miami"
                maxLength={120}
                className="rounded-xl"
                data-testid="post-focus-keyword-input"
              />
              <p className="text-xs text-muted-foreground">
                Tip: Use 2–5 words your ideal client would type into Google. Be specific.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="post-secondary-keywords">Secondary Keywords</Label>
              <KeywordTagInput
                id="post-secondary-keywords"
                value={form.secondary_keywords}
                onChange={(next) => setForm((prev) => ({ ...prev, secondary_keywords: next }))}
                placeholder="e.g. ketamine therapy, trauma recovery, virtual therapy"
                testId="post-secondary-keywords"
              />
              <p className="text-xs text-muted-foreground">
                Press Enter or type a comma to add each one (up to 20). Added to the post&rsquo;s meta keywords tag and article schema.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="post-image">Featured Image URL</Label>
              <Input
                id="post-image"
                value={form.image_url}
                onChange={handleField("image_url")}
                placeholder="https://..."
                className="rounded-xl"
                data-testid="post-image-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="post-image-alt">
                Image Alt Text{" "}
                <span className="text-xs text-muted-foreground font-normal">
                  (describes the image for screen readers & Google Images)
                </span>
              </Label>
              <Input
                id="post-image-alt"
                value={form.image_alt}
                onChange={handleField("image_alt")}
                placeholder="e.g. Woman sitting peacefully on a couch during a ketamine therapy session"
                maxLength={160}
                className="rounded-xl"
                data-testid="post-image-alt-input"
              />
              <p className="text-xs text-muted-foreground">
                Tip: Describe what is in the image and include your focus keyword naturally if it fits.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="post-excerpt">Excerpt (shows on blog listing card)</Label>
              <Textarea
                id="post-excerpt"
                value={form.excerpt}
                onChange={handleField("excerpt")}
                placeholder="Auto-generated from content if left blank"
                rows={2}
                className="rounded-xl"
                data-testid="post-excerpt-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="post-meta">
                  SEO Meta Description{" "}
                  <span className="text-xs text-muted-foreground font-normal">
                    (Google search snippet)
                  </span>
                </Label>
                <span
                  className={`text-xs ${charCountColor}`}
                  data-testid="meta-char-count"
                >
                  {charCount} / 160 (max 170)
                </span>
              </div>
              <Textarea
                id="post-meta"
                value={form.meta_description}
                onChange={handleField("meta_description")}
                placeholder="A compelling 1–2 sentence summary that will appear in Google search results and social media share previews. Aim for ~150–160 characters."
                rows={3}
                maxLength={170}
                className="rounded-xl"
                data-testid="post-meta-input"
              />
              <p className="text-xs text-muted-foreground">
                If left blank, the excerpt will be used as the meta description.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Label htmlFor="post-content">Content *</Label>
                <div className="flex items-center gap-2 flex-wrap" data-testid="content-toolbar">
                  <button
                    type="button"
                    onClick={() => wrapSelection("**", "**")}
                    className="h-8 w-8 rounded-lg border border-border bg-white hover:bg-secondary/40 inline-flex items-center justify-center font-bold text-foreground"
                    title="Bold (wrap selection in ** ... **)"
                    data-testid="content-bold-btn"
                  >
                    <Bold className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => wrapSelection("*", "*")}
                    className="h-8 w-8 rounded-lg border border-border bg-white hover:bg-secondary/40 inline-flex items-center justify-center italic text-foreground"
                    title="Italic (wrap selection in * ... *)"
                    data-testid="content-italic-btn"
                  >
                    <Italic className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={insertLink}
                    className="h-8 w-8 rounded-lg border border-border bg-white hover:bg-secondary/40 inline-flex items-center justify-center text-foreground"
                    title="Insert link (internal path or external URL)"
                    data-testid="content-link-btn"
                  >
                    <Link2 className="h-4 w-4" />
                  </button>
                  <div className="h-6 w-px bg-border mx-1"></div>
                  <select
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return;
                      setHeading(v === "p" ? 0 : Number(v));
                      e.target.value = "";
                    }}
                    defaultValue=""
                    className="h-8 px-2 rounded-lg border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="content-heading-select"
                    title="Turn the current line(s) into a heading"
                  >
                    <option value="" disabled>Heading</option>
                    <option value="2">Heading 2</option>
                    <option value="3">Heading 3</option>
                    <option value="p">Normal text</option>
                  </select>
                  <select
                    onChange={(e) => {
                      const size = e.target.value;
                      if (!size) return;
                      wrapSelection(`[${size}]`, `[/${size}]`);
                      e.target.value = ""; // reset so user can re-pick same size
                    }}
                    defaultValue=""
                    className="h-8 px-2 rounded-lg border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="content-size-select"
                    title="Wrap selection with a font size"
                  >
                    <option value="" disabled>Text size</option>
                    <option value="sm">Small</option>
                    <option value="lg">Large</option>
                    <option value="xl">Extra large</option>
                  </select>
                  <div className="h-6 w-px bg-border mx-1"></div>
                  <div className="flex items-center gap-1.5">
                    <Type className="h-3.5 w-3.5 text-muted-foreground" />
                    <select
                      value={form.content_font}
                      onChange={handleField("content_font")}
                      className="h-8 px-2 rounded-lg border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid="content-font-select"
                    >
                      <option value="">Default font</option>
                      <option value="serif">Serif (elegant)</option>
                      <option value="sans">Sans-serif (modern)</option>
                      <option value="mono">Monospace</option>
                    </select>
                  </div>
                </div>
              </div>
              <Textarea
                ref={contentRef}
                id="post-content"
                value={form.content}
                onChange={handleField("content")}
                placeholder="Write your blog post here. Separate paragraphs with a blank line."
                rows={16}
                className="rounded-xl font-sans"
                data-testid="post-content-input"
              />
              <p className="text-xs text-muted-foreground">
                Tip: Highlight text and click <strong>B</strong>, <em>I</em>, the{" "}
                <span className="font-medium">link</span> icon, or pick a{" "}
                <span className="font-medium">Text size</span> to format it. You can
                also type <code className="text-[11px] bg-muted px-1 rounded">**bold**</code>,{" "}
                <code className="text-[11px] bg-muted px-1 rounded">*italic*</code>,{" "}
                <code className="text-[11px] bg-muted px-1 rounded">[link text](/path)</code>, or{" "}
                <code className="text-[11px] bg-muted px-1 rounded">[lg]big[/lg]</code>,{" "}
                <code className="text-[11px] bg-muted px-1 rounded">## Heading 2</code> or{" "}
                <code className="text-[11px] bg-muted px-1 rounded">### Heading 3</code> (on its own line) by hand.
              </p>
            </div>

            {/* Live rendered preview */}
            <div className="space-y-2" data-testid="content-live-preview">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm">Live Preview</Label>
                <button
                  type="button"
                  onClick={() => setShowPreview((v) => !v)}
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                  data-testid="toggle-preview-btn"
                >
                  {showPreview ? (
                    <>
                      <EyeOff className="h-3.5 w-3.5" /> Hide
                    </>
                  ) : (
                    <>
                      <Eye className="h-3.5 w-3.5" /> Show
                    </>
                  )}
                </button>
              </div>
              {showPreview && (
                <div className="rounded-xl border border-border bg-white p-6 max-h-[420px] overflow-y-auto">
                  {form.content.trim() ? (
                    <PostContent
                      content={form.content}
                      contentFont={form.content_font}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      Start typing above — your post will render here exactly as visitors will see it.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="post-author">Author</Label>
              <Input
                id="post-author"
                value={form.author}
                onChange={handleField("author")}
                className="rounded-xl"
                data-testid="post-author-input"
              />
            </div>

            <SeoAnalysisPanel form={form} />

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="save-post-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {getSaveLabel()}
              </Button>
              <Button variant="outline" onClick={cancelEdit} className="rounded-full">
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    );
  }

  // === List View ===
  return (
    <div data-testid="blog-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-xl font-semibold text-foreground">Blog Posts</h2>
          <p className="text-sm text-muted-foreground">
            {posts.length} published post{posts.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPosts}
            disabled={loading}
            className="rounded-full"
            data-testid="refresh-posts-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            onClick={startNew}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="new-post-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New Post
          </Button>
        </div>
      </div>

      {posts.length === 0 ? (
        <Card className="bg-white rounded-2xl p-12 text-center">
          <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">
            No blog posts yet. Create your first post to get started.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <Card
              key={post.id}
              className="bg-white rounded-2xl p-5 flex gap-4 items-start"
              data-testid={`blog-post-row-${post.slug}`}
            >
              {post.image_url && (
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="h-20 w-20 object-cover rounded-xl flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground truncate">{post.title}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                  {post.excerpt}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  {post.meta_description ? (
                    <Badge className="bg-green-100 text-green-800 border-0 text-xs">
                      SEO ✓
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-xs">
                      No SEO meta
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground">/{post.slug}</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => startEdit(post)}
                className="rounded-full flex-shrink-0"
                data-testid={`edit-post-${post.slug}`}
              >
                <Edit className="h-4 w-4 mr-1" /> Edit
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogManager;
